import axios from "axios";

import { ensureCsrfToken } from "../../../../lib/csrf";

const ANALYSIS_URL = "/api/digital-risk/portfolio-decision/analysis/";

const resolveProjection = (projection, byId) => {
  if (!projection) return projection;
  const positions = (projection.positionIds || []).map((id) => byId.get(id)).filter(Boolean);
  return {
    ...projection,
    ...(projection.positionIds ? { positions } : {}),
    ...(projection.evaluatedPositionIds ? { evaluatedPositions: projection.evaluatedPositionIds.map((id) => byId.get(id)).filter(Boolean) } : {}),
    ...(projection.compliantPositionIds ? { compliantPositions: projection.compliantPositionIds.map((id) => byId.get(id)).filter(Boolean) } : {}),
    lowerBound: byId.get(projection.lowerBoundId),
    upperBound: byId.get(projection.upperBoundId),
    minimumFundedReservePosition: byId.get(projection.minimumFundedReservePositionId),
    maximumModeledReservePosition: byId.get(projection.maximumModeledReservePositionId),
    firstCompliantPosition: byId.get(projection.firstCompliantPositionId),
    closestAppetitePosition: byId.get(projection.closestAppetitePositionId),
  };
};

/** Restore the historical in-memory view with shared references, not JSON copies. */
export const hydrateCompactFrontier = (frontier) => {
  if (frontier?.contractVersion !== "compact-v1") return frontier;
  const positions = frontier.frontierPositions || [];
  const byId = new Map(positions.map((position) => [position.positionId, position]));
  const capitalEfficientRange = resolveProjection(frontier.capitalEfficientRange, byId);
  return {
    ...frontier,
    positions,
    maximumModeledProtection: frontier.maximumModeledProtection
      ? { ...byId.get(frontier.maximumModeledProtection.positionId), ...frontier.maximumModeledProtection }
      : null,
    capitalEfficientRange,
    efficientRange: capitalEfficientRange,
    retentionProtection: resolveProjection(frontier.retentionProtection, byId),
    appetiteAchievement: resolveProjection(frontier.appetiteAchievement, byId),
  };
};

export const hydratePortfolioAnalysis = (data) => data?.capitalEfficiencyFrontier
  ? { ...data, capitalEfficiencyFrontier: hydrateCompactFrontier(data.capitalEfficiencyFrontier) }
  : data;

export const fetchPortfolioAnalysis = async ({
  scenarioIds,
  objective = "CAPITAL_AT_RISK_REDUCTION",
  simulations,
  seed = null,
  approvedBudget = null,
  optimizationMode = "LEGACY",
}) => {
  const normalizedIds = [...new Set((scenarioIds || []).map(Number))].filter(
    (id) => Number.isInteger(id) && id > 0
  );
  const normalizedSimulations = Number(simulations);

  if (!normalizedIds.length) throw new Error("At least one scenario is required.");
  if (!Number.isInteger(normalizedSimulations) || normalizedSimulations <= 0) {
    throw new Error("Monte Carlo simulations must be a positive integer.");
  }

  const csrfToken = await ensureCsrfToken();
  const payload = {
    scenario_ids: normalizedIds,
    objective,
    simulations: normalizedSimulations,
    seed: seed === null || seed === undefined || seed === "" ? null : Number(seed),
    optimization_mode: optimizationMode,
  };
  if (approvedBudget !== null && approvedBudget !== undefined && approvedBudget !== "") {
    payload.approved_budget = Number(approvedBudget);
  }
  const { data } = await axios.post(ANALYSIS_URL, payload, {
    withCredentials: true,
    headers: { "X-CSRFToken": csrfToken },
  });
  return hydratePortfolioAnalysis(data);
};
